import { SkyDialog, SkyMessageBar, SkyRadioGroup, SkyText } from "@skypointcloud/skypoint-ui";
import { byCodeUnit } from "@shared/sort";
import type { ConnectorAccessMode, ConnectorAccessView } from "@shared/types/connectorAccess";

/**
 * The tenant's connector choice as a form (T-51, round 10 C1): one row per connector with a
 * three-way control, Enabled, Disabled or Hidden, and a "New connectors start as" choice for any
 * connector added to skyData later. Settings > Connectors (the TenantAdmin) and the Tenants page
 * (a PlatformAdmin) both render it, so the two read the same words.
 *
 * - Enabled: offered for new connections.
 * - Disabled: shown greyed in the New connection picker with a reason; cannot be picked.
 * - Hidden: not shown to the tenant at all.
 * Existing connections keep working whatever a connector's state.
 *
 * The stored choice (`mode`, `connectors`, `hidden`) has no "all except these disabled": with
 * "start as Enabled" (`all`) a connector is Enabled or Hidden. Choosing Disabled for a row while
 * that is on switches the start to Disabled (`selected`) and lists every other shown connector, so
 * nothing else changes state.
 *
 * `options` are the connectors one may set: the available ones for a TenantAdmin, every one for
 * a PlatformAdmin. A stored name that is not among them is kept by the server and not shown.
 */

export interface ConnectorChoiceDraft {
  mode: ConnectorAccessMode;
  connectors: string[];
  hidden: string[];
}

export type ConnectorRowState = "enabled" | "disabled" | "hidden";

export interface ConnectorChoiceOption {
  name: string;
  displayName: string;
  /** Said under the name, such as "Not available" on the PlatformAdmin's list. */
  description?: string;
}

export function draftFromView(view: ConnectorAccessView): ConnectorChoiceDraft {
  return { mode: view.mode, connectors: [...view.connectors], hidden: [...view.hidden] };
}

function sameList(a: string[], b: string[]): boolean {
  const left = [...a].sort(byCodeUnit);
  const right = [...b].sort(byCodeUnit);
  return left.length === right.length && left.every((name, index) => name === right[index]);
}

export function sameChoice(a: ConnectorChoiceDraft, b: ConnectorChoiceDraft): boolean {
  if (a.mode !== b.mode || !sameList(a.hidden, b.hidden)) return false;
  if (a.mode === "all") return true;
  return sameList(a.connectors, b.connectors);
}

/** One connector's state in a draft. */
export function stateOf(draft: ConnectorChoiceDraft, name: string): ConnectorRowState {
  if (draft.hidden.includes(name)) return "hidden";
  if (draft.mode === "all") return "enabled";
  return draft.connectors.includes(name) ? "enabled" : "disabled";
}

/** The draft after one row is set; see the component note for what Disabled does under `all`. */
export function withState(
  draft: ConnectorChoiceDraft,
  options: ConnectorChoiceOption[],
  name: string,
  state: ConnectorRowState,
): ConnectorChoiceDraft {
  const without = (list: string[]) => list.filter((entry) => entry !== name);
  if (state === "hidden") {
    return {
      ...draft,
      connectors: without(draft.connectors),
      hidden: [...without(draft.hidden), name],
    };
  }
  const hidden = without(draft.hidden);
  if (state === "enabled") {
    return {
      ...draft,
      hidden,
      connectors:
        draft.mode === "all" ? draft.connectors : [...new Set([...draft.connectors, name])],
    };
  }
  if (draft.mode === "all") {
    // Every other shown connector keeps being Enabled, now by name.
    const others = options
      .map((option) => option.name)
      .filter((other) => other !== name && !draft.hidden.includes(other));
    const kept = draft.connectors.filter((entry) => !options.some((o) => o.name === entry));
    return { mode: "selected", connectors: [...new Set([...kept, ...others])], hidden };
  }
  return { ...draft, hidden, connectors: without(draft.connectors) };
}

/** "Start as" changes the mode and nothing a row already says. */
export function withStartAs(
  draft: ConnectorChoiceDraft,
  options: ConnectorChoiceOption[],
  mode: ConnectorAccessMode,
): ConnectorChoiceDraft {
  if (mode === draft.mode) return draft;
  if (mode === "all") return { ...draft, mode };
  // all -> selected: the connectors shown as Enabled stay Enabled, by name.
  const enabled = options
    .map((option) => option.name)
    .filter((name) => !draft.hidden.includes(name));
  const kept = draft.connectors.filter((entry) => !options.some((o) => o.name === entry));
  return { ...draft, mode, connectors: [...new Set([...kept, ...enabled])] };
}

/** "Disabled" start with none of the shown ones Enabled: no new connection at all. */
export function offersNone(draft: ConnectorChoiceDraft, options: ConnectorChoiceOption[]): boolean {
  return !options.some((option) => stateOf(draft, option.name) === "enabled");
}

const ROW_STATES = [
  { value: "enabled", label: "Enabled" },
  { value: "disabled", label: "Disabled" },
  { value: "hidden", label: "Hidden" },
];

export function ConnectorChoiceFields({
  draft,
  options,
  onChange,
  readOnly = false,
  tenantName,
  testIdPrefix,
}: {
  draft: ConnectorChoiceDraft;
  options: ConnectorChoiceOption[];
  onChange: (draft: ConnectorChoiceDraft) => void;
  readOnly?: boolean;
  tenantName: string;
  testIdPrefix: string;
}) {
  const enabled = options.filter((option) => stateOf(draft, option.name) === "enabled").length;
  return (
    <div className="flex flex-col gap-3">
      <SkyRadioGroup
        testId={`${testIdPrefix}-mode`}
        label="New connectors start as"
        name={`${testIdPrefix}-mode`}
        value={draft.mode}
        disabled={readOnly}
        onChange={(value) => onChange(withStartAs(draft, options, value as ConnectorAccessMode))}
        options={[
          {
            value: "all",
            label: "Enabled",
            description: "A connector added to skyData later is offered at once.",
          },
          {
            value: "selected",
            label: "Disabled",
            description: "A connector added to skyData later is not offered until it is enabled.",
          },
        ]}
      />

      <div data-testid={`${testIdPrefix}-list`} className="flex flex-col gap-2">
        <SkyText size={300} weight="semibold" block>
          {`Connectors (${enabled} of ${options.length} enabled)`}
        </SkyText>
        <ul className="flex flex-col divide-y divide-border">
          {options.map((option) => (
            <li
              key={option.name}
              className="flex flex-wrap items-center justify-between gap-2 py-2"
              data-testid={`${testIdPrefix}-row-${option.name}`}
            >
              <div className="min-w-0">
                <SkyText weight="semibold" block>
                  {option.displayName}
                </SkyText>
                {option.description ? (
                  <SkyText size={200} color="muted" block>
                    {option.description}
                  </SkyText>
                ) : null}
              </div>
              <SkyRadioGroup
                ariaLabel={`${option.displayName} state`}
                name={`${testIdPrefix}-state-${option.name}`}
                layout="horizontal"
                size="small"
                options={ROW_STATES}
                value={stateOf(draft, option.name)}
                disabled={readOnly}
                onChange={(value) =>
                  onChange(withState(draft, options, option.name, value as ConnectorRowState))
                }
                testId={`${testIdPrefix}-state-${option.name}`}
              />
            </li>
          ))}
        </ul>
      </div>

      {!readOnly && offersNone(draft, options) ? (
        <SkyMessageBar
          intent="warning"
          layout="multiline"
          testId={`${testIdPrefix}-none-warning`}
          title="No connector enabled"
          message={`Saved like this, nobody in ${tenantName} can create a new connection. Existing connections keep working.`}
        />
      ) : null}
    </div>
  );
}

/** The confirmation before saving a choice that offers no connector. */
export function OffersNoneDialog({
  open,
  onOpenChange,
  onConfirm,
  tenantName,
  testId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  tenantName: string;
  testId: string;
}) {
  return (
    <SkyDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Offer no connectors?"
      description={`Nobody in ${tenantName} will be able to create a new connection until a connector is selected again. Existing connections keep working.`}
      size="small"
      testId={testId}
      primaryAction={{ label: "Save with no connectors", onClick: onConfirm, destructive: true }}
      secondaryAction={{ label: "Cancel", onClick: () => onOpenChange(false) }}
    />
  );
}
